import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, IconButton, Link, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { embedUrl, watchUrl } from "./youtubeApi";

interface YouTubePlaybackProps {
    videoId: string | null;
    /** Thumbnail from search results; shown as a poster until the user starts playback. */
    thumbnail?: string;
    title?: string;
}

const YouTubePlayback: React.FC<YouTubePlaybackProps> = ({ videoId, thumbnail, title }) => {
    const [hasError, setHasError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // Reset playback state whenever the selected video changes.
    useEffect(() => {
        setHasError(false);
        setIsPlaying(false);
    }, [videoId]);

    if (!videoId) {
        return (
            <Box
                data-testid="youtube-playback"
                sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 1,
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Select a video to play it here.
                </Typography>
            </Box>
        );
    }

    if (hasError) {
        return (
            <Box
                data-testid="youtube-playback"
                sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 1,
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    textAlign: "center",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    This video can't be played here.
                </Typography>
                <Link
                    href={watchUrl(videoId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-attr="youtube_play-video"
                    data-custom-id={videoId}
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                    Watch on YouTube <OpenInNewIcon fontSize="small" />
                </Link>
            </Box>
        );
    }

    // Poster: thumbnail + play control (no iframe until the user opts in — no autoplay).
    if (!isPlaying) {
        return (
            <Box
                data-testid="youtube-playback"
                data-attr="youtube_play-video"
                data-custom-id={videoId}
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                }}
            >
                {thumbnail ? (
                    <Box
                        component="img"
                        src={thumbnail}
                        alt={title || "Video thumbnail"}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            {title || "Video ready to play"}
                        </Typography>
                    </Box>
                )}
                <IconButton
                    aria-label="Play video"
                    onClick={() => setIsPlaying(true)}
                    data-attr="youtube_start-playback"
                    data-custom-id={videoId}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 64,
                        height: 64,
                        backgroundColor: "rgba(0, 0, 0, 0.65)",
                        color: "common.white",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                    }}
                >
                    <PlayArrowIcon sx={{ fontSize: 40 }} />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box
            data-testid="youtube-playback"
            data-attr="youtube_play-video"
            data-custom-id={videoId}
            sx={{ width: "100%", aspectRatio: "16 / 9", borderRadius: 1, overflow: "hidden" }}
        >
            <iframe
                key={videoId}
                width="100%"
                height="100%"
                src={embedUrl(videoId)}
                title={title || "YouTube video player"}
                style={{ border: 0, display: "block" }}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                onError={() => setHasError(true)}
            />
        </Box>
    );
};

export default YouTubePlayback;
